import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/userService';
import { useSupabaseAuthClient } from '../../hooks/useSupabaseAuthClient';
import supabase from '../../services/supabaseClient';
import { useTranslation } from '../../i18n';
import './Login.css';

const Login = ({ handleSignIn }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const { state, dispatch } = useAuth();
  const client = useSupabaseAuthClient();

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!email.trim()) {
      errors.email = t('login.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('login.emailInvalid');
    }

    if (!password) {
      errors.password = t('login.passwordRequired');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReg = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSending(true);
    setError('');
    try {
      const response = await UserService.loginWithClient(client, { email, password }, dispatch);
      if (response.isLoggedIn) {
        setSending(false);
        handleSignIn({ success: true });
      }
    } catch (error) {
      setSending(false);
      handleError(error);
    }
  };

  const handleError = (error) => {
    if (error.status === 403) {
      setError({ emailNotVerified: true });
      return;
    }
    setError(error ? error.message : t('login.unexpectedError'));
  };

  const handleGoogleSignIn = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        throw error;
      }
      // User will be redirected to Google, no need to setSending(false) here
    } catch (error) {
      setSending(false);
      handleError(error);
    }
  };

  const resendConfirmationEmail = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      await UserService.resendConfirmationEmail({ username: email, password });
      setSending(false);
    } catch (error) {
      setSending(false);
      handleError(error);
    }
  };

  const renderError = () => {
    if (error) {
      if (error.emailNotVerified) {
        return (
          <div className="alert alert-danger" role="alert">
            <span>
              {t('login.emailNotVerified1')} <a href="#" onClick={resendConfirmationEmail}>{t('login.emailNotVerifiedHere')}</a>{t('login.emailNotVerified2')}
            </span>
          </div>
        );
      }
      return (
        <div className="alert alert-danger" role="alert">{error}</div>
      );
    }
    return null;
  };

  return (
    <form className="form-signin">
      <h1 className="h3 mb-3 font-weight-normal text-center">{t('login.title')}</h1>
      <div className="form-box">
        <div className="form-group">
          <label htmlFor="inputEmail">{t('login.email')}</label>
          <input
            type="email"
            required=""
            className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
            id="inputEmail"
            value={email}
            onChange={handleEmailChange}
            disabled={sending}
          />
          {validationErrors.email && (
            <div className="invalid-feedback d-block">{validationErrors.email}</div>
          )}
        </div>
        <div className="form-group">
          <Link tabIndex="2" className="forgot" to="/forgotPassword">{t('login.forgotPassword')}</Link>
          <label htmlFor="password">{t('login.password')}</label>
          <input
            type="password"
            required=""
            className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
            id="password"
            value={password}
            onChange={handlePasswordChange}
            disabled={sending}
          />
          {validationErrors.password && (
            <div className="invalid-feedback d-block">{validationErrors.password}</div>
          )}
        </div>
        <hr className="mt-5 mb-4" />
        {renderError()}
        {sending ?
          (<div className="progress">
            <div className="progress-bar w-100 progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow="75" aria-valuemin="0" aria-valuemax="100" />
          </div>)
          :
          (
            <>
              <button className="btn btn-bs btn-primary w-100 mb-3" type="submit" onClick={handleReg}>{t('login.signIn')}</button>
              <div className="text-center mb-2">
                <span className="text-muted">{t('login.or')}</span>
              </div>
              <button 
                className="btn btn-outline-secondary w-100" 
                type="button" 
                onClick={handleGoogleSignIn}
                disabled={sending}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg className="mr-2" width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </svg>
                  <span>{t('login.continueWithGoogle')}</span>
                </span>
              </button>
            </>
          )
        }
      </div>
      <div className="form-box form-box-small">
        <Link to="/signup">{t('login.createAccountLink')}</Link>
      </div>
    </form>
  );
};

export default Login;
